import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

export const getCurrentUser = createAsyncThunk(
    'user/getCurrent',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get('/user/get-my-profile', { withCredentials: true });
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch user');
        }
    }
);

export const getPassengers = createAsyncThunk(
    'user/getPassengers',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get('/user/passengers', { withCredentials: true });
            console.log("Passenger API response (user):", res.data);
            return res.data.passengerList;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch passengers');
        }
    }
);

export const getPassengersByAdmin = createAsyncThunk(
    'user/getPassengersByAdmin',
    async ({ userId }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`/user/passengers/admin/${userId}`, { withCredentials: true });
            console.log("Passenger API response (admin):", res.data);
            return res.data.passengerList;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch passengers');
        }
    }
);

export const addPassenger = createAsyncThunk(
    'user/addPassenger',
    async (data, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post('/user/passengers', data, { withCredentials: true });
            return res.data.passengerList;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to add passenger');
        }
    }
);

export const updatePassenger = createAsyncThunk(
    'user/updatePassenger',
    async ({ email, data }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(`/user/passengers/${email}`, data, { withCredentials: true });
            return res.data.passengerList;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to update passenger');
        }
    }
);

export const deletePassenger = createAsyncThunk(
    'user/deletePassenger',
    async (email, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete(`/user/passengers/${email}`, { withCredentials: true });
            return res.data.passengerList;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to delete passenger');
        }
    }
);

export const updateUserProfile = createAsyncThunk(
    'user/updateProfile',
    async (formData, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put('/user/update-profile', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Update failed');
        }
    }
);

export const getAllUsers = createAsyncThunk(
    'user/getAllUsers',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get('/user/all-user', { withCredentials: true });
            return res.data.users;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Unable to fetch users');
        }
    }
);

export const getUserById = createAsyncThunk(
    'user/getUserById',
    async (id, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`/user/${id}`, { withCredentials: true });
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'User not found');
        }
    }
);

export const upsertAddress = createAsyncThunk(
    'user/upsertAddress',
    async (addressData, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put('/user/address', addressData, { withCredentials: true });
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to update address');
        }
    }
);

export const deleteAddress = createAsyncThunk(
    'user/deleteAddress',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete('/user/address', { withCredentials: true });
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to delete address');
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState: {
        userInfo: null,
        passengerList: [],
        usersList: [],
        selectedUser: null,
        selectedPassengerList: [],
        loading: false,
        error: null,
        success: false,
    },
    reducers: {
        clearUserState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCurrentUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.loading = false;
                state.userInfo = action.payload;
                state.passengerList = action.payload?.passengerList || [];
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getPassengers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getPassengers.fulfilled, (state, action) => {
                state.loading = false;
                state.passengerList = action.payload || [];
            })
            .addCase(getPassengers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getPassengersByAdmin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getPassengersByAdmin.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedPassengerList = action.payload || [];
            })
            .addCase(getPassengersByAdmin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(addPassenger.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(addPassenger.fulfilled, (state, action) => {
                state.loading = false;
                state.passengerList = action.payload;
                state.success = true;
            })
            .addCase(addPassenger.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updatePassenger.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updatePassenger.fulfilled, (state, action) => {
                state.loading = false;
                state.passengerList = action.payload;
                state.success = true;
            })
            .addCase(updatePassenger.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deletePassenger.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deletePassenger.fulfilled, (state, action) => {
                state.loading = false;
                state.passengerList = action.payload;
                state.success = true;
            })
            .addCase(deletePassenger.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.userInfo = action.payload;
                state.success = true;
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getAllUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.usersList = action.payload;
            })
            .addCase(getAllUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getUserById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedUser = action.payload;
            })
            .addCase(getUserById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(upsertAddress.fulfilled, (state, action) => {
                state.userInfo = action.payload;
            })
            .addCase(deleteAddress.fulfilled, (state, action) => {
                state.userInfo = action.payload;
            });
    },
});

export const { clearUserState } = userSlice.actions;
export default userSlice.reducer;